import { useEffect, useRef, useState } from "react";
import {
  type ConsentValue,
  consentToGtagConsentPayload,
  readConsentCookieFromDocument,
  writeConsentCookie,
} from "@/lib/consent";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

/** Von CookieSettingsLink / UserButton-Menüpunkt genutzt, um den Banner erneut zu öffnen. */
export const REOPEN_CONSENT_BANNER_EVENT = "propora:open-consent-banner";

export function openConsentBanner() {
  window.dispatchEvent(new Event(REOPEN_CONSENT_BANNER_EVENT));
}

/**
 * Pusht den Consent-Status NUR bei einer aktiven Entscheidung (Klick), nicht beim
 * Laden. Wiederkehrende Besucher mit Cookie bekommen granted/denied bereits über
 * den consent-default-Inline-Script in index.html vor dem GTM-Load -- der normale
 * All-Pages-Trigger übernimmt dann, ohne dass hier zusätzlich etwas feuert.
 */
function pushConsentDecision(value: ConsentValue) {
  window.gtag?.("consent", "update", consentToGtagConsentPayload(value));
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "consent_update",
    analytics: value.analytics,
    marketing: value.marketing,
  });
}

export function ConsentBanner() {
  const [visible, setVisible] = useState(false);
  const bannerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setVisible(!readConsentCookieFromDocument());

    function handleReopen() {
      setVisible(true);
    }
    window.addEventListener(REOPEN_CONSENT_BANNER_EVENT, handleReopen);
    return () => window.removeEventListener(REOPEN_CONSENT_BANNER_EVENT, handleReopen);
  }, []);

  // Reserviert Platz am Seitenende, solange der Banner sichtbar ist -- sonst kann er
  // (fixed, zIndex 9999, volle Breite) interaktive Elemente am unteren Bildschirmrand
  // ueberdecken, ohne dass das als Overlay auffaellt. Live-Befund 10.10.2026: Das
  // Cloudflare-Turnstile-Kaestchen auf /register wurde angezeigt, reagierte aber nicht
  // auf Klicks -- in Inkognito (keine Consent-Cookie) ist dieser Banner beim ersten
  // Aufruf immer sichtbar, und die /register-Seite ist seit dem Entfernen von Avataren/
  // Testimonials/zweitem Haken kuerzer geworden, sodass das Formularende naeher am
  // unteren Bildschirmrand liegt als vorher. Hoehe wird gemessen statt geschaetzt, da
  // der Text je nach Breite umbricht (ResizeObserver, nicht nur beim Mount).
  useEffect(() => {
    if (!visible) {
      document.body.style.paddingBottom = "";
      return;
    }
    const el = bannerRef.current;
    if (!el) return;
    function applyPadding() {
      if (el) document.body.style.paddingBottom = `${el.offsetHeight}px`;
    }
    applyPadding();
    const ro = new ResizeObserver(applyPadding);
    ro.observe(el);
    window.addEventListener("resize", applyPadding);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", applyPadding);
      document.body.style.paddingBottom = "";
    };
  }, [visible]);

  function decide(analytics: boolean, marketing: boolean) {
    const value: ConsentValue = { analytics, marketing, ts: new Date().toISOString() };
    writeConsentCookie(value);
    pushConsentDecision(value);
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      ref={bannerRef}
      role="dialog"
      aria-live="polite"
      aria-label="Cookie-Einstellungen"
      style={{
        position: "fixed",
        insetInline: 0,
        bottom: 0,
        zIndex: 9999,
        borderTop: "1px solid #d4d9ca",
        background: "#f5f3ea",
        backdropFilter: "blur(12px)",
        boxShadow: "0 -8px 24px rgba(23,61,48,0.12)",
        padding: "20px 16px",
      }}
    >
      <div className="mx-auto flex max-w-5xl flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="max-w-2xl text-sm leading-relaxed" style={{ color: "#5e6d60" }}>
          Wir nutzen Cookies für Analyse und Marketing, um PROPORA zu verbessern. Du entscheidest,
          welche Kategorien wir setzen dürfen. Mehr dazu in unserer{" "}
          <a href="https://www.propora.de/datenschutz" className="underline hover:opacity-80">
            Datenschutzerklärung
          </a>
          .
        </p>
        <div className="flex flex-shrink-0 gap-3">
          <button
            type="button"
            data-testid="consent-reject-all"
            onClick={() => decide(false, false)}
            className="rounded-[10px] px-5 py-2.5 text-sm font-semibold transition-colors hover:bg-black/5"
            style={{ border: "1px solid #d4d9ca", color: "#173d30" }}
          >
            Ablehnen
          </button>
          <button
            type="button"
            data-testid="consent-accept-all"
            onClick={() => decide(true, true)}
            className="rounded-[10px] px-5 py-2.5 text-sm font-extrabold transition-transform hover:scale-[1.02]"
            style={{ background: "#244737", color: "#fffdf8" }}
          >
            Alle akzeptieren
          </button>
        </div>
      </div>
    </div>
  );
}
